/**
 * feat-078 — Rehearsal Test Harness (admin in-browser grading sandbox).
 *
 * Presentational full-page body. It is opened in a SEPARATE browser window
 * (`window.open` → the `/quiz-management/rehearsal-test/:rehearsalId` route, see
 * RehearsalTestHarnessPage) so authors can edit curriculum and run the harness
 * side-by-side — the original modal blocked the editor underneath.
 *
 * That `/quiz-management/...` path is NOT a missed rename: the four browser
 * routes are EXCLUDED BY NAME from feat-198 (ADR 0001 §D-M) because a
 * bookmarked URL is user state. API paths moved; browser routes did not.
 *
 * The author curates / AI-generates sample statements a real server might say,
 * runs them through the **real** grader (the same `grade_transcript` code path
 * as live grading — ADR 0001 §N1), and sees per-statement pass/fail + the exact
 * coaching feedback a server would receive, plus per-criterion hit/miss with
 * evidence and actual-vs-expected mismatch flags.
 *
 * Hard guarantees carried from the spec / ADR:
 *  - **Grades against the SAVED rehearsal** (decision #2): only opened for an
 *    already-saved rehearsal; grades against its pinned prompt version. The
 *    separate-window model fits cleanly — edit + Save in the editor window, then
 *    Run-all here re-fetches and grades the saved state.
 *  - **Zero side effects** (N2 / AC7): `test-grade` is stateless — no attempt
 *    rows, no XP. Run results are bookkept on the suite table only, via PATCH
 *    of `last_passed`/`last_run_at`/`last_language`.
 *  - **Run-all fans out N single `test-grade` calls** with a frontend
 *    concurrency pool capped at 4 (ADR §D4); results stream in as each lands and
 *    one statement's soft-fail never sinks the batch.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  X,
  Play,
  Plus,
  Sparkles,
  Trash2,
  ChevronUp,
  ChevronDown,
  Check,
  AlertTriangle,
  Loader2,
  History,
  RefreshCw,
} from 'lucide-react';
import {
  listTestStatements,
  createTestStatement,
  updateTestStatement,
  deleteTestStatement,
  reorderTestStatements,
  generateTestStatements,
  testGradeRehearsal,
  listRehearsalAttempts,
  ExpectedVerdict,
  RehearsalTestLanguage,
  RehearsalTestStatement,
  RehearsalTestGradeResult,
  RehearsalAttempt,
} from '../../services/quizContentAdminService';

// ---- concurrency ----

/** Run `worker` over `items` with at most `limit` in flight at once. Each
 *  worker is responsible for its own error handling (we never reject the pool
 *  on a single failure — partial-failure isolation, ADR §D4). */
async function runPool<T>(
  items: T[],
  limit: number,
  worker: (item: T) => Promise<void>,
): Promise<void> {
  let cursor = 0;
  const next = async (): Promise<void> => {
    const i = cursor++;
    if (i >= items.length) return;
    await worker(items[i]);
    return next();
  };
  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, () => next()),
  );
}

const RUN_POOL_LIMIT = 4; // ADR §D4
const GENERATE_COUNT = 6; // ADR §D3.2 default

// ---- row model ----

/** A suite statement plus per-session transient state (the last grade result
 *  and the in-flight flag). `id` is a real uuid for persisted rows or a
 *  `temp-…` placeholder for a freshly-added, not-yet-saved row. */
interface Row extends RehearsalTestStatement {
  result?: RehearsalTestGradeResult;
  running?: boolean;
}

let tempCounter = 0;
const newTempId = () => `temp-${tempCounter++}`;
const isTemp = (id: string) => id.startsWith('temp-');

function makeTempRow(position: number): Row {
  return {
    id: newTempId(),
    rehearsal_template_id: '',
    statement: '',
    expected_verdict: 'unset',
    source: 'manual',
    last_passed: null,
    last_run_at: null,
    last_language: null,
    position,
  };
}

// ---- mismatch logic ----

type MatchState = 'match' | 'mismatch' | 'na';

/** The verdict to compare/render for a row: the fresh in-session grade if the
 *  row was run this session, else the persisted `last_passed` from a prior run
 *  (so a reopened suite still shows its last results — AC8). */
function effectivePassed(row: Row): boolean | null | undefined {
  if (row.result) return row.result.passed;
  return row.last_passed; // boolean | null (null == prior soft-fail) | absent
}

/** Compare a row's effective verdict against its author-set expectation.
 *  `na` when there's no verdict yet, no expectation, or a soft-fail. */
function matchState(row: Row): MatchState {
  const passed = effectivePassed(row);
  if (passed === undefined || passed === null) return 'na';
  if (row.expected_verdict === 'unset') return 'na';
  const expectedPass = row.expected_verdict === 'pass';
  return expectedPass === passed ? 'match' : 'mismatch';
}

/** Compact relative time ("just now", "5m ago", "3h ago", "2d ago") for the
 *  persisted last-run timestamp so a reopened suite shows when it last ran.
 *  Falls back to an empty string on an unparseable value. */
function formatTimeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const secs = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (secs < 45) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

// ============================================================
// Component
// ============================================================

export const RehearsalTestHarness: React.FC<{
  rehearsalId: string;
  rehearsalName: string;
  pinnedVersionNumber: number | null;
  /** Optional — renders a "Close window" affordance (the route page wires this
   *  to `window.close()`). Omit when the harness is embedded without a window. */
  onClose?: () => void;
}> = ({ rehearsalId, rehearsalName, pinnedVersionNumber, onClose }) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [language, setLanguage] = useState<RehearsalTestLanguage>('en');
  const [generating, setGenerating] = useState(false);
  const [runningAll, setRunningAll] = useState(false);
  const [hasRun, setHasRun] = useState(false);

  // Async closures (blur autosave, reorder, delete) need the live rows; keep a
  // ref synced to the latest rendered value. Assigned during render (the
  // documented "latest value" ref pattern) — not in an effect — so it is never
  // a render behind, which matters the moment after the suite first loads.
  const rowsRef = useRef<Row[]>(rows);
  rowsRef.current = rows;

  // Baselines for last-persisted statement text, so blur only PATCHes on a
  // real change (avoids redundant writes).
  const savedTextRef = useRef<Record<string, string>>({});

  // In-flight create promises keyed by temp id, so the blur-autosave and a Run
  // that race on the same fresh row don't double-create it.
  const persistingRef = useRef<Map<string, Promise<string>>>(new Map());

  const patchRow = useCallback((id: string, patch: Partial<Row>) => {
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }, []);

  /** Ensure a row exists server-side, creating it if it's still a temp
   *  placeholder, and return its real id. Deduplicates concurrent callers (blur
   *  autosave + Run) via the in-flight map so a row is never double-created. A
   *  temp row with empty text can't be created and resolves to its temp id.
   *  This is what lets a freshly-added-then-run statement record its
   *  last_run_at — without it the run-result PATCH was skipped for temp rows
   *  (the !isTemp guard) and the result vanished on reopen. */
  const ensurePersisted = useCallback(
    (rowId: string): Promise<string> => {
      if (!isTemp(rowId)) return Promise.resolve(rowId);
      const inFlight = persistingRef.current.get(rowId);
      if (inFlight) return inFlight;
      const row = rowsRef.current.find((r) => r.id === rowId);
      const trimmed = row?.statement.trim() ?? '';
      if (!row || !trimmed) return Promise.resolve(rowId);
      const p = (async () => {
        const created = await createTestStatement(rehearsalId, {
          statement: trimmed,
          expected_verdict: row.expected_verdict,
          source: row.source,
          position: row.position,
        });
        savedTextRef.current[created.id] = created.statement;
        delete savedTextRef.current[rowId];
        setRows((rs) =>
          rs.map((r) => (r.id === rowId ? { ...created, result: r.result } : r)),
        );
        return created.id;
      })().finally(() => persistingRef.current.delete(rowId));
      persistingRef.current.set(rowId, p);
      return p;
    },
    [rehearsalId],
  );

  // ---- initial load ----
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await listTestStatements(rehearsalId);
        if (cancelled) return;
        const loaded: Row[] = list.map((s) => ({ ...s }));
        loaded.forEach((s) => {
          savedTextRef.current[s.id] = s.statement;
        });
        setRows(loaded);
      } catch (e) {
        if (!cancelled) setError(`Couldn't load saved statements: ${String(e)}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [rehearsalId]);

  // ---- persistence helpers ----

  /** Persist a row's statement text on blur. Creates the row if it's still a
   *  temp placeholder (and swaps in the server id), otherwise PATCHes. Empty
   *  text is never persisted. */
  const persistStatement = useCallback(
    async (rowId: string) => {
      const row = rowsRef.current.find((r) => r.id === rowId);
      if (!row) return;
      const trimmed = row.statement.trim();
      if (!trimmed) return;
      if (savedTextRef.current[rowId] === trimmed) return;
      try {
        if (isTemp(rowId)) {
          await ensurePersisted(rowId);
        } else {
          const updated = await updateTestStatement(rehearsalId, rowId, {
            statement: trimmed,
          });
          savedTextRef.current[rowId] = updated.statement;
          setRows((rs) =>
            rs.map((r) => (r.id === rowId ? { ...r, ...updated } : r)),
          );
        }
      } catch (e) {
        setError(`Couldn't save statement: ${String(e)}`);
      }
    },
    [rehearsalId, ensurePersisted],
  );

  /** Set a row's expected verdict and persist immediately (PATCH for saved
   *  rows; temp rows just hold it locally until their text is first saved). */
  const setExpected = useCallback(
    async (rowId: string, expected: ExpectedVerdict) => {
      patchRow(rowId, { expected_verdict: expected });
      if (isTemp(rowId)) return;
      try {
        await updateTestStatement(rehearsalId, rowId, {
          expected_verdict: expected,
        });
      } catch (e) {
        setError(`Couldn't save expected verdict: ${String(e)}`);
      }
    },
    [patchRow, rehearsalId],
  );

  const addManual = useCallback(() => {
    setRows((rs) => [...rs, makeTempRow(rs.length)]);
  }, []);

  const deleteRow = useCallback(
    async (rowId: string) => {
      const row = rowsRef.current.find((r) => r.id === rowId);
      setRows((rs) => rs.filter((r) => r.id !== rowId));
      delete savedTextRef.current[rowId];
      if (row && !isTemp(rowId)) {
        try {
          await deleteTestStatement(rehearsalId, rowId);
        } catch (e) {
          setError(`Couldn't delete statement: ${String(e)}`);
        }
      }
    },
    [rehearsalId],
  );

  /** Move a row up/down. Reorders locally, then persists the new order of the
   *  saved rows (temp rows keep their index-derived position when saved). */
  const moveRow = useCallback(
    async (idx: number, delta: number) => {
      const target = idx + delta;
      if (target < 0 || target >= rowsRef.current.length) return;
      const next = [...rowsRef.current];
      [next[idx], next[target]] = [next[target], next[idx]];
      next.forEach((r, i) => (r.position = i));
      setRows(next);
      const realIds = next.filter((r) => !isTemp(r.id)).map((r) => r.id);
      if (realIds.length > 1) {
        try {
          await reorderTestStatements(rehearsalId, realIds);
        } catch (e) {
          setError(`Couldn't save order: ${String(e)}`);
        }
      }
    },
    [rehearsalId],
  );

  // ---- AI generation ----
  const generate = useCallback(async () => {
    setGenerating(true);
    setError(null);
    try {
      const candidates = await generateTestStatements(rehearsalId, {
        count: GENERATE_COUNT,
      });
      // Persist each candidate as a suite row (source='ai'); the author can
      // override the expected verdict or delete unwanted ones afterward.
      let position = rowsRef.current.length;
      for (const c of candidates) {
        try {
          const created = await createTestStatement(rehearsalId, {
            statement: c.statement,
            expected_verdict: c.expected_verdict,
            source: 'ai',
            position: position++,
          });
          savedTextRef.current[created.id] = created.statement;
          setRows((rs) => [...rs, { ...created }]);
        } catch (e) {
          setError(`Couldn't save a generated statement: ${String(e)}`);
        }
      }
    } catch (e) {
      setError(`AI generation failed: ${String(e)}`);
    } finally {
      setGenerating(false);
    }
  }, [rehearsalId]);

  // ---- grading ----

  /** Grade one row through the real grader, stream the result into state, and
   *  persist the run result onto the suite row (keeps grading itself pure).
   *  Takes the statement text by value (callers pass the on-screen text) so it
   *  never depends on `rowsRef` being synced — robust against mount timing. */
  const gradeRow = useCallback(
    async (rowId: string, rawStatement: string) => {
      const statement = rawStatement.trim();
      if (!statement) return;
      // Persist the row FIRST so a freshly-added temp statement has a real id to
      // record its run result against (otherwise the !isTemp guard below skips
      // the write and the result is lost on reopen). Also avoids a temp→real id
      // swap racing the grade.
      let id = rowId;
      try {
        id = await ensurePersisted(rowId);
      } catch (e) {
        setError(`Couldn't save the statement before testing: ${String(e)}`);
        return;
      }
      patchRow(id, { running: true });
      try {
        const result = await testGradeRehearsal(rehearsalId, {
          statement,
          language,
        });
        patchRow(id, { result, running: false });
        // Persist the run result onto the suite row (AC8) for saved rows. The
        // server auto-stamps last_run_at when last_passed is sent; fold the
        // returned row (incl. the stamped last_run_at) back into state so a
        // reopen shows it. SURFACE a failure rather than swallow it — the grade
        // is already shown, but a silent persist-fail (e.g. a blocked PATCH)
        // would otherwise make the result vanish on reopen with no clue why.
        if (!isTemp(id)) {
          try {
            const saved = await updateTestStatement(rehearsalId, id, {
              last_passed: result.passed,
              last_language: language,
            });
            patchRow(id, {
              last_passed: saved.last_passed,
              last_run_at: saved.last_run_at,
              last_language: saved.last_language,
            });
          } catch (e) {
            setError(
              `Graded, but couldn't save the result (it won't show on reopen): ${String(e)}`,
            );
          }
        }
      } catch (e) {
        patchRow(id, { running: false });
        setError(`Grade failed: ${String(e)}`);
      }
    },
    [rehearsalId, language, patchRow, ensurePersisted],
  );

  const runAll = useCallback(async () => {
    // Grade every non-empty statement as currently shown (the on-screen text;
    // grading uses the rehearsal's SAVED criteria/prompt server-side — decision
    // #2 — but the statement under test is whatever the author has typed).
    // rowsRef is synced during render, so it always reflects the loaded suite.
    const gradable = rowsRef.current.filter((r) => r.statement.trim());
    if (gradable.length === 0) return;
    setRunningAll(true);
    setError(null);
    setHasRun(true);
    await runPool(gradable, RUN_POOL_LIMIT, (r) => gradeRow(r.id, r.statement));
    setRunningAll(false);
  }, [gradeRow]);

  // ---- run summary (this session's grades only) ----
  const summary = useMemo(() => {
    let expected = 0;
    let matched = 0;
    let mismatched = 0;
    let softFail = 0;
    for (const r of rows) {
      if (!r.result) continue; // only count statements graded this session
      if (r.result.passed === null) {
        softFail++;
        continue;
      }
      if (r.expected_verdict === 'unset') continue;
      expected++;
      if ((r.expected_verdict === 'pass') === r.result.passed) matched++;
      else mismatched++;
    }
    return { expected, matched, mismatched, softFail };
  }, [rows]);

  const anyGradable = rows.some((r) => r.statement.trim());

  return (
    <>
    {/* The stateless grading sandbox — unchanged (feat-078). The "Recent
        Attempts" history (feat-110) is an additive sibling below it; it loads
        independently so it never blocks or alters the sandbox. */}
    <div
      data-testid="rehearsal-test-harness"
      className="flex flex-col bg-white"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <div className="min-w-0">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Play className="w-4 h-4 text-purple-600" />
            Test harness
          </h3>
          <p className="text-xs text-gray-500 truncate">
            Grades “{rehearsalName || 'rehearsal'}” against the saved version
            {pinnedVersionNumber != null && ` · Pinned prompt v${pinnedVersionNumber}`}
            {' · no attempts or XP are created'}
          </p>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 border border-gray-300 rounded px-2 py-1 ml-2"
          >
            <X className="w-3.5 h-3.5" />
            Close window
          </button>
        )}
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 p-3 border-b bg-gray-50">
          <button
            onClick={runAll}
            disabled={runningAll || !anyGradable}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {runningAll ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Play className="w-4 h-4" />
            )}
            Run all
          </button>

          <button
            onClick={generate}
            disabled={generating}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded border border-purple-300 text-purple-700 hover:bg-purple-50 disabled:opacity-40"
          >
            {generating ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4" />
            )}
            Generate with AI
          </button>

          <button
            onClick={addManual}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded border border-gray-300 text-gray-700 hover:bg-gray-100"
          >
            <Plus className="w-4 h-4" />
            Add statement
          </button>

          {/* EN/ES coaching-language toggle (AC6) */}
          <div className="ml-auto flex items-center gap-1 text-xs">
            <span className="text-gray-500">Coaching</span>
            <div className="inline-flex rounded border border-gray-300 overflow-hidden">
              {(['en', 'es'] as RehearsalTestLanguage[]).map((lng) => (
                <button
                  key={lng}
                  onClick={() => setLanguage(lng)}
                  className={`px-2 py-1 uppercase ${
                    language === lng
                      ? 'bg-purple-600 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {lng}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Run summary */}
        {hasRun && summary.expected + summary.softFail > 0 && (
          <div className="px-3 py-2 border-b text-xs flex items-center gap-3 flex-wrap">
            <span className="text-gray-700">
              <strong>{summary.matched}</strong>/{summary.expected} as expected
            </span>
            {summary.mismatched > 0 && (
              <span className="inline-flex items-center gap-1 text-orange-700">
                <AlertTriangle className="w-3.5 h-3.5" />
                {summary.mismatched} mismatch{summary.mismatched > 1 ? 'es' : ''}
              </span>
            )}
            {summary.softFail > 0 && (
              <span className="text-amber-600">
                {summary.softFail} couldn’t grade
              </span>
            )}
          </div>
        )}

        {error && (
          <div className="m-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-800">
            {error}
          </div>
        )}

        {/* Body */}
        <div className="p-3 space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-gray-400">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center py-10 text-sm text-gray-500">
              No statements yet. <strong>Generate with AI</strong> for a
              strong/weak/edge mix, or <strong>Add statement</strong> to write
              your own, then <strong>Run all</strong> to see how the grader
              scores them.
            </div>
          ) : (
            rows.map((row, idx) => (
              <StatementRow
                key={row.id}
                row={row}
                idx={idx}
                total={rows.length}
                onText={(text) => patchRow(row.id, { statement: text })}
                onTextBlur={() => persistStatement(row.id)}
                onExpected={(v) => setExpected(row.id, v)}
                onMove={(delta) => moveRow(idx, delta)}
                onDelete={() => deleteRow(row.id)}
                onRun={() => gradeRow(row.id, row.statement)}
              />
            ))
          )}
        </div>
    </div>

    <RecentAttemptsSection rehearsalId={rehearsalId} />
    </>
  );
};

// ============================================================
// feat-110 — Recent Attempts history (read-only)
// ============================================================
//
// A read-only window into REAL, saved `rehearsal_attempt` rows for this drill,
// across all users — the opposite of the stateless sandbox above. Renders the
// 10 most recent (the endpoint returns up to 200; we cap client-side). Loads
// independently of the sandbox (its own fetch/state) and degrades gracefully on
// error so a failed attempts load never breaks grading — same spirit as the POS
// table's try/catch in ShiftTranscriptView. No ids are ever surfaced (spec #5).

const ATTEMPTS_CAP = 10; // spec §2 — never render all 200

/** Pass/fail/soft-fail badge for an attempt. Reuses the harness's VerdictBadge
 *  styling (same color classes) but with past-tense "Passed/Failed" wording per
 *  the spec's display copy. `null` ⇒ soft-fail "Couldn't grade". */
const AttemptBadge: React.FC<{ passed: boolean | null }> = ({ passed }) => {
  if (passed === null) {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-medium text-xs">
        <AlertTriangle className="w-3 h-3" /> Couldn’t grade
      </span>
    );
  }
  return passed ? (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-green-100 text-green-800 font-medium text-xs">
      <Check className="w-3 h-3" /> Passed
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-100 text-red-800 font-medium text-xs">
      <X className="w-3 h-3" /> Failed
    </span>
  );
};

const AttemptCard: React.FC<{ attempt: RehearsalAttempt }> = ({ attempt }) => {
  const when = attempt.started ?? attempt.ended;
  const transcript = attempt.transcript_text?.trim();
  // On a soft-fail there's no coaching; surface the analyzer_error instead so the
  // reason it couldn't grade is visible (spec §display).
  const feedback =
    attempt.passed === null
      ? attempt.analyzer_error || 'The grader returned no verdict.'
      : attempt.ai_coaching_text?.trim();

  return (
    <div className="border border-gray-200 rounded p-3 space-y-2 bg-white">
      <div className="flex items-center gap-2">
        <AttemptBadge passed={attempt.passed} />
        {when && (
          <span
            className="text-[11px] text-gray-400 whitespace-nowrap"
            title={new Date(when).toLocaleString()}
          >
            {formatTimeAgo(when)}
          </span>
        )}
      </div>

      {/* Spoken transcript — always shown, wrapped (never clipped). */}
      <div className="text-sm text-gray-800 whitespace-pre-wrap break-words">
        {transcript ? (
          transcript
        ) : (
          <span className="text-gray-400 italic">(no transcript)</span>
        )}
      </div>

      {/* AI praise (flavor, passes only) then coaching / soft-fail reason. */}
      {attempt.passed === true && attempt.ai_praise_text?.trim() && (
        <div className="text-xs bg-green-50 border border-green-200 rounded p-2 text-green-800">
          <span className="font-medium">Praise: </span>
          {attempt.ai_praise_text.trim()}
        </div>
      )}
      <div
        className={`text-xs rounded p-2 ${
          attempt.passed === null
            ? 'bg-amber-50 border border-amber-200 text-amber-700'
            : 'bg-gray-50 border border-gray-200 text-gray-700'
        }`}
      >
        <span className="font-medium">
          {attempt.passed === null ? 'Why it couldn’t grade: ' : 'Coaching: '}
        </span>
        {feedback || (
          <span className="text-gray-400 italic">(no feedback)</span>
        )}
      </div>
    </div>
  );
};

/** Read-only "Recent Attempts" section. Self-contained fetch + state so it loads
 *  lazily and a failure here never touches the sandbox above. */
const RecentAttemptsSection: React.FC<{ rehearsalId: string }> = ({
  rehearsalId,
}) => {
  const [attempts, setAttempts] = useState<RehearsalAttempt[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const list = await listRehearsalAttempts(rehearsalId);
        if (cancelled) return;
        // Cap at 10 client-side (spec §2) — the endpoint returns up to 200.
        setAttempts(list.slice(0, ATTEMPTS_CAP));
      } catch (e) {
        if (!cancelled) setError(String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [rehearsalId, reloadKey]);

  return (
    <section
      data-testid="recent-attempts"
      className="max-w-3xl mx-auto w-full px-4 py-6"
    >
      <div className="flex items-center gap-2 mb-1">
        <History className="w-4 h-4 text-purple-600" />
        <h3 className="text-lg font-semibold">Recent attempts</h3>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        The 10 most recent real attempts on this rehearsal, across all staff.
      </p>

      {loading ? (
        <div className="flex items-center justify-center py-10 text-gray-400">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading attempts…
        </div>
      ) : error ? (
        // Inline, retryable — does NOT break the sandbox above (graceful
        // degrade, POS-table try/catch spirit).
        <div className="p-3 bg-red-50 border border-red-200 rounded text-xs text-red-800 flex items-center justify-between gap-3">
          <span>Couldn’t load recent attempts: {error}</span>
          <button
            onClick={() => setReloadKey((k) => k + 1)}
            className="inline-flex items-center gap-1 px-2 py-1 rounded border border-red-300 text-red-700 hover:bg-red-100 whitespace-nowrap"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Retry
          </button>
        </div>
      ) : attempts && attempts.length > 0 ? (
        <div className="space-y-3">
          {attempts.map((a) => (
            <AttemptCard key={a.id} attempt={a} />
          ))}
        </div>
      ) : (
        <div className="text-center py-10 text-sm text-gray-500">
          No one has attempted this rehearsal yet.
        </div>
      )}
    </section>
  );
};

// ============================================================
// Statement row
// ============================================================

const VERDICT_OPTIONS: { value: ExpectedVerdict; label: string }[] = [
  { value: 'pass', label: 'Pass' },
  { value: 'fail', label: 'Fail' },
  { value: 'unset', label: '—' },
];

const StatementRow: React.FC<{
  row: Row;
  idx: number;
  total: number;
  onText: (text: string) => void;
  onTextBlur: () => void;
  onExpected: (v: ExpectedVerdict) => void;
  onMove: (delta: number) => void;
  onDelete: () => void;
  onRun: () => void;
}> = ({ row, idx, total, onText, onTextBlur, onExpected, onMove, onDelete, onRun }) => {
  const ms = matchState(row);
  const result = row.result;

  return (
    <div
      className={`border rounded p-3 space-y-2 ${
        ms === 'mismatch'
          ? 'border-orange-300 bg-orange-50'
          : 'border-gray-200 bg-white'
      }`}
    >
      {/* Top controls */}
      <div className="flex items-center gap-2 text-xs">
        <div className="flex flex-col">
          <button
            onClick={() => onMove(-1)}
            disabled={idx === 0}
            className="text-gray-400 hover:text-gray-700 disabled:opacity-30"
            aria-label="Move up"
          >
            <ChevronUp className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => onMove(1)}
            disabled={idx === total - 1}
            className="text-gray-400 hover:text-gray-700 disabled:opacity-30"
            aria-label="Move down"
          >
            <ChevronDown className="w-3.5 h-3.5" />
          </button>
        </div>

        {row.source === 'ai' && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">
            <Sparkles className="w-3 h-3" /> AI
          </span>
        )}

        {/* Expected verdict */}
        <span className="text-gray-500 ml-1">Expected:</span>
        <div className="inline-flex rounded border border-gray-300 overflow-hidden">
          {VERDICT_OPTIONS.map((o) => (
            <button
              key={o.value}
              onClick={() => onExpected(o.value)}
              className={`px-2 py-0.5 ${
                row.expected_verdict === o.value
                  ? 'bg-gray-700 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>

        {/* Actual verdict badge — fresh grade, or a muted prior-run badge (plus
            a "last run · Xm ago" line) when reopening a suite that was run
            earlier (AC8). */}
        <div className="ml-auto flex items-center gap-2">
          {result ? (
            <VerdictBadge passed={result.passed} />
          ) : row.last_run_at ? (
            <span
              className="inline-flex items-center gap-1.5 opacity-70"
              title={`Last run ${new Date(row.last_run_at).toLocaleString()}`}
            >
              <VerdictBadge passed={row.last_passed} />
              <span className="text-[11px] text-gray-400 whitespace-nowrap">
                last run {formatTimeAgo(row.last_run_at)}
              </span>
            </span>
          ) : null}
          {ms === 'mismatch' && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-orange-200 text-orange-800 font-medium">
              <AlertTriangle className="w-3 h-3" /> Mismatch
            </span>
          )}
          <button
            onClick={onRun}
            disabled={row.running || !row.statement.trim()}
            className="inline-flex items-center gap-1 px-2 py-1 rounded border border-purple-300 text-purple-700 hover:bg-purple-50 disabled:opacity-40"
            title="Re-run this statement"
          >
            {row.running ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Play className="w-3.5 h-3.5" />
            )}
            Run
          </button>
          <button
            onClick={onDelete}
            className="text-gray-400 hover:text-red-600"
            aria-label="Delete statement"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Statement text */}
      <textarea
        className="w-full border rounded px-2 py-1 text-sm resize-y min-h-[2.5rem]"
        placeholder="What a server might say…"
        value={row.statement}
        onChange={(e) => onText(e.target.value)}
        onBlur={onTextBlur}
        rows={2}
      />

      {/* Result detail */}
      {result && <GradeDetail result={result} />}
    </div>
  );
};

const VerdictBadge: React.FC<{ passed: boolean | null }> = ({ passed }) => {
  if (passed === null) {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-medium">
        <AlertTriangle className="w-3 h-3" /> Couldn’t grade
      </span>
    );
  }
  return passed ? (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-green-100 text-green-800 font-medium">
      <Check className="w-3 h-3" /> Pass
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-100 text-red-800 font-medium">
      <X className="w-3 h-3" /> Fail
    </span>
  );
};

const GradeDetail: React.FC<{ result: RehearsalTestGradeResult }> = ({
  result,
}) => {
  // Soft-fail: distinct "couldn't grade" state, never a silent fail.
  if (result.passed === null) {
    return (
      <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2 text-amber-800">
        <div className="font-medium mb-0.5">Couldn’t grade this statement</div>
        <div className="text-amber-700">
          {result.analyzer_error || 'The grader returned no verdict.'}
        </div>
      </div>
    );
  }

  return (
    <div className="text-xs space-y-2">
      {result.passed && result.praise_text && (
        <div className="bg-green-50 border border-green-200 rounded p-2 text-green-800">
          <span className="font-medium">Praise: </span>
          {result.praise_text}
        </div>
      )}
      {result.coaching_text && (
        <div className="bg-gray-50 border border-gray-200 rounded p-2 text-gray-700">
          <span className="font-medium">Coaching: </span>
          {result.coaching_text}
        </div>
      )}

      {result.criterion_hits.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b text-left text-gray-500">
                <th className="py-1 pr-2 w-10">Hit</th>
                <th className="py-1 pr-2">Criterion</th>
                <th className="py-1 pr-2">Evidence</th>
              </tr>
            </thead>
            <tbody>
              {result.criterion_hits.map((c) => (
                <tr key={c.criterion_id} className="border-b align-top">
                  <td className="py-1 pr-2">
                    {c.hit ? (
                      <Check className="w-4 h-4 text-green-600" />
                    ) : (
                      <X className="w-4 h-4 text-red-500" />
                    )}
                  </td>
                  <td className="py-1 pr-2">
                    <span
                      className={`inline-block px-1 rounded mr-1 ${
                        c.kind === 'good'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {c.kind === 'good' ? 'good' : 'bad'}
                    </span>
                    {c.label}
                  </td>
                  <td className="py-1 pr-2 text-gray-600 italic">
                    {c.evidence_quote ? `“${c.evidence_quote}”` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default RehearsalTestHarness;
